import { type Entry } from "contentful";

import { type ContentType } from "./schemes/content.scheme";
import {
    isTypeBlogArticlePage,
    isTypeBlogCategoryPage,
    isTypeDefaultPage,
    isTypeSearchPage,
    type TypeBlogArticlePageSkeleton,
    type TypeBlogCategoryPageSkeleton,
    type TypeDefaultPageSkeleton,
    type TypeRedirectSkeleton,
    type TypeSearchPageSkeleton,
} from "./types";

export enum H_TAGS {
    h1 = "h1",
    h2 = "h2",
    h3 = "h3",
    h4 = "h4",
    h5 = "h5",
    h6 = "h6",
}

export enum VARIANTS {
    a = "A",
    b = "B",
}

export enum CONTENT_TYPES {
    anchorModule = "anchorModule",
    bannerModule = "bannerModule",
    blogArticlePage = "blogArticlePage",
    blogCategoryPage = "blogCategoryPage",
    defaultPage = "defaultPage",
    faecher = "faecher",
    footerModule = "footerModule",
    formDownloadModule = "formDownloadModule",
    formEdupageNewsletterModule = "formEdupageNewsletterModule",
    formFreelanceModule = "FormFreelanceModule",
    formNewsletterModule = "formNewsletterModule",
    formSupportModule = "formSupportModule",
    headerModule = "headerModule",
    navItem = "navItem",
    productPage = "productPage",
    redirect = "redirect",
    searchPage = "searchPage",
    settings = "settings",
}

export enum NAVIGATION_TYPE {
    headerPrimary,
    headerSecondary,
    footerPrimary,
    footerSecondary,
}

export enum DEVICE_BREAKPOINTS {
    min = 0,
    lg = 1080,
    md = 920,
    xs = 420,
    sm = 768,
    xl = 1280,
}
export enum BREAKPOINTS {
    lg = 1080,
    "lg-1/2" = BREAKPOINTS.lg / 2,
    md = 920,
    "md-1/2" = BREAKPOINTS.md / 2,
    xs = 420,
    sm = 768,
    "sm-1/2" = BREAKPOINTS.sm / 2,
    xl = 1280,
    "xl-1/2" = BREAKPOINTS.xl / 2,
    viewport = BREAKPOINTS.xl,
    "viewport-1/2" = BREAKPOINTS.viewport / 2,
    "viewport-1/3" = BREAKPOINTS.viewport / 3,
    "viewport-1/4" = BREAKPOINTS.viewport / 4,
}

export enum COLORS {
    backgroundModal = "rgba(0, 0, 0, 0.50)",
    blue = "rgba(150, 190, 230, 1)", // #69BEEF
    blue1 = "rgba(13, 71, 161, 1)",
    blue2 = "rgba(11, 94, 130, 1)",
    blueSoft = "rgba(210, 235, 245, 1)", // #D2EBF5
    blueSoftHover = "rgba(194, 218, 226, 1)",
    brandDark = "rgba(47, 55, 58, 1)",
    brandWhiteHpver = "rgba(255, 255, 255, 0.20)",
    brandYellow = "rgba(255, 213, 0, 1)", // #FFD500
    green = "rgba(130, 195, 135, 1)", // #82C387
    green1 = "rgba(0, 102, 70, 1)",
    green2 = "rgba(42, 88, 23, 1)",
    greenLight = "rgba(80, 158, 47, 1)",
    greenSoft = "rgba(218, 237, 219, 1)", // #DAEDDB
    grey1 = "rgba(245, 245, 245, 1)",
    grey2 = "rgba(235, 235, 235, 1)",
    grey3 = "rgba(224, 224, 224, 1)",
    grey4 = "rgba(209, 209, 209, 1)",
    grey5 = "rgba(198, 198, 198, 1)",
    grey6 = "rgba(184, 184, 184, 1)",
    grey7 = "rgba(173, 184, 184, 1)",
    greyDark = "rgba(89, 95, 97, 1)", // #595F61
    greyHexagon = "rgba(233, 236, 235, 1)",
    greyOpacity05 = "rgba(47, 55, 58, 0.05)",
    greyOpacity10 = "rgba(47, 55, 58, 0.1)",
    greyWireframe = "rgba(47, 47, 47, 1)",
    modalBackground = "rgba(0, 0, 0, 0.25)",
    orange = "rgba(255, 154, 59, 1)", // #FF9A3B
    orangeSoft = "rgba(255, 220, 187, 1)", // #FFDCBB
    pink = "rgba(156, 22, 142, 1)",
    purple = "rgba(205, 175, 255, 1)", // #CDAFFF
    purpleSoft = "rgba(238, 227, 255, 1)", // #EEE3FF
    red = "rgba(171, 26, 61, 1)",
    redError = "rgba(168, 0, 34, 1)",
    rose = "rgba(255, 175, 191, 1)",
    transparent = "transparent",
    violet = "rgba(80, 8, 120, 1)",
    white = "rgba(255, 255, 255, 1)",
    whiteOpacity20 = "rgba(255, 255, 255, 0.2)",
    yellowDarker = "rgba(232, 201, 71, 1)",
    yellowSoft = "rgba(255, 245, 175, 1)", // #FFF5AF
}

export enum BACKGROUND_COLORS {
    blue = "blue",
    blueSoft = "blueSoft",
    brandYellow = "brandYellow",
    green = "green",
    greenSoft = "greenSoft",
    orange = "orange",
    orangeSoft = "orangeSoft",
    purple = "purple",
    purpleSoft = "purpleSoft",
    white = "white",
    yellowSoft = "yellowSoft",
}

export const BACKGROUND_CLASSES: Record<BACKGROUND_COLORS, `bg-${keyof typeof COLORS}`> = {
    [BACKGROUND_COLORS.blueSoft]: "bg-blueSoft",
    [BACKGROUND_COLORS.blue]: "bg-blue",
    [BACKGROUND_COLORS.white]: "bg-white",
    [BACKGROUND_COLORS.green]: "bg-green",
    [BACKGROUND_COLORS.greenSoft]: "bg-greenSoft",
    [BACKGROUND_COLORS.purple]: "bg-purple",
    [BACKGROUND_COLORS.purpleSoft]: "bg-purpleSoft",
    [BACKGROUND_COLORS.orange]: "bg-orange",
    [BACKGROUND_COLORS.orangeSoft]: "bg-orangeSoft",
    [BACKGROUND_COLORS.brandYellow]: "bg-brandYellow",
    [BACKGROUND_COLORS.yellowSoft]: "bg-yellowSoft",
};

export const uuid = (): string => Math.random().toString(16).slice(2);

// Is Submodule e.g. Testimonial in RichText. Use number for grid if supported
export type EMBEDDED = boolean | number;

export const BLOG_ARTICLES_PER_PAGE = 10;

export enum STATIC_PATH {
    api = "/api",
    auth = "/auth",
    magazin = "/magazin",
    p = "/p",
    root = "/",
    search = "/suchergebnisse",
    searchCategoryFilterPath = "/unterrichtsmaterial/f",
    searchCategoryPath = "/unterrichtsmaterial",
    status404 = "/status/404",
}

export const NOT_ALLOWED_DEFAULT_PAGES_PREFIX: string[] = [
    STATIC_PATH.api,
    STATIC_PATH.auth,
    STATIC_PATH.magazin,
    STATIC_PATH.p,
    STATIC_PATH.searchCategoryPath,
    STATIC_PATH.status404,
    STATIC_PATH.search,
];

export enum SEARCH_PARAMS {
    entries = "entries",
    faecher = "faecher",
    klassenstufen = "klassenstufen",
    page = "page",
    productVariant = "productVariant",
    schularten = "schularten",
    search = "search",
    kategorie = "kategorie",
    sortType = "sortType",
    searchTerm = "searchTerm",
    entriesPerPage = "entriesPerPage",
}

export const DEFAULT_SEARCH_ENTRIES_PER_PAGE = 18;

export const MAX_SEARCH_TERM_LENGTH = 255;

export const SECTION_TOP_SPACING = {
    none: (embedded?: EMBEDDED) => (embedded ? "" : "-mt-16"),
    normal: (embedded?: EMBEDDED) => (embedded ? "mt-16" : ""),
    small: (embedded?: EMBEDDED) => (embedded ? "mt-8" : "-mt-8"),
};

export const SECTION_BOTTOM_SPACING = {
    none: (embedded?: EMBEDDED) => (embedded ? "" : "-mb-16"),
    normal: (embedded?: EMBEDDED) => (embedded ? "mb-16" : ""),
    small: (embedded?: EMBEDDED) => (embedded ? "mb-8" : "-mb-8"),
};

export const getProductURL = (title?: string, rNumber?: string): string => {
    if (rNumber) {
        const slug = getProductSlug(title, rNumber);
        return `${STATIC_PATH.p}/${slug}`;
    } else if (title) {
        return `${STATIC_PATH.p}/${title}`;
    }
    return STATIC_PATH.p;
};

export const getBlogURL = (
    categorySlug?: string,
    articleSlug?: string,
    pageNumber?: number | string,
): string => {
    if (articleSlug && categorySlug) {
        return STATIC_PATH.magazin + categorySlug + articleSlug;
    } else if (categorySlug) {
        return STATIC_PATH.magazin + categorySlug + (pageNumber ? `/seite/${pageNumber}` : "");
    } else {
        return STATIC_PATH.magazin + (pageNumber ? `/seite/${pageNumber}` : "");
    }
};

export const getSearchURL = (path?: string): string => {
    if (path) {
        return STATIC_PATH.searchCategoryPath + path;
    } else {
        return STATIC_PATH.searchCategoryPath;
    }
};

export const getDynamicSearchURL = (path?: string): string => {
    if (path) {
        return STATIC_PATH.searchCategoryFilterPath + path;
    } else {
        return STATIC_PATH.searchCategoryFilterPath;
    }
};

// Alternativ: text.replace(/(?<!\s)-(?!\s)/g, "\u00AD");
export const formatText = (text: string) => text.replace(/\\-/g, "\u00AD");

export const getPageURL = (
    ref:
        | Entry<
              | TypeRedirectSkeleton
              | TypeBlogArticlePageSkeleton
              | TypeBlogCategoryPageSkeleton
              | TypeDefaultPageSkeleton
              | TypeSearchPageSkeleton,
              "WITHOUT_UNRESOLVABLE_LINKS",
              string
          >
        | undefined,
    href: string | undefined,
    context: Context,
): string | undefined => {
    // Explicitly check whether "fields" is invalid. This could be due to "draft" references.
    if (ref?.fields) {
        if (isTypeDefaultPage(ref)) {
            return [ref.fields.slug, href].filter(Boolean).join("");
        } else if (isTypeBlogCategoryPage(ref)) {
            return [getBlogURL(ref.fields.slug), href].filter(Boolean).join("");
        } else if (isTypeSearchPage(ref)) {
            const path = context.searchPagePathsById.get(ref.sys.id)?.values().next().value;
            return [getSearchURL(path), href].filter(Boolean).join("");
            // Explicitly check whether "fields" is invalid. This could be due to "draft" references.
        } else if (isTypeBlogArticlePage(ref) && ref.fields.categoryRef?.fields) {
            return [getBlogURL(ref.fields.categoryRef.fields.slug, ref.fields.slug), href]
                .filter(Boolean)
                .join("");
        }
    }
    return href;
};

export function getLinkTarget(link: string | undefined): string | undefined {
    if (link) {
        if (link.startsWith("http:")) {
            return "_blank";
        }
        if (link.startsWith("mailto:")) {
            return undefined;
        }

        const clean = link.split(/[?#]/)[0]; // remove query + hash
        const hasFileEnding = /\.[^/.]+$/.test(clean);
        if (hasFileEnding) {
            return "_blank";
        }
    }
    return undefined;
}

export const getProductSlug = (title: string | undefined, rNumber: string): string => {
    const slug = [];

    if (title) {
        slug.push(
            (title ?? "")
                ?.toLowerCase() // Convert to lowercase
                .trim() // Trim leading and trailing spaces
                .replace(/ä/gi, "ae") // Replace ä/Ä with ae
                .replace(/ö/gi, "oe") // Replace ö/Ö with oe
                .replace(/ü/gi, "ue") // Replace ü/Ü with ue
                .replace(/ß/gi, "ss") // Replace ß with ss (optional, depending on your needs)
                .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
                .replace(/\s+/g, "-") // Replace spaces with hyphens
                .replace(/-+/g, "-"),
        );
    }
    slug.push(rNumber);
    return slug.join("-");
};

export const getRNumberFromSlug = (slug: string | undefined): string | undefined => {
    const match = slug?.match(/R\d{4}-\d{6}$/);
    if (match) {
        return match[0];
    } else {
        return undefined;
    }
};

export const toErrorString = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    } else if (
        error instanceof Object &&
        "message" in error &&
        (typeof error.message === "string" || error.message instanceof String)
    ) {
        return error.message.toString();
    } else if (typeof error === "string" || error instanceof String) {
        return error.toString();
    } else {
        return "";
    }
};

export const isString = (str: unknown): str is string =>
    typeof str === "string" || str instanceof String;

export function clone<T extends object>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}
/*
Splits an Array in n chuncks: ([1,2,3,4],2) => [[1,2],[3,4]]
*/

export const arrayChunks = <T>(arr: T[], n: number): T[][] => {
    const size = Math.ceil(arr.length / n);
    return Array.from({ length: n }, (_v, i) => arr.slice(i * size, i * size + size));
};

export const removeFalsy = <T>(t: T | undefined): t is T => {
    return !!t;

    // Explicitly check whether "fields" is invalid. This could be due to "draft" references.
    //  return typeof t !== "object" || ("fields" in t && !!t.fields);
};

const FORM_MAX_LENGTH: Record<string, number> = {
    default: 60,
    message: 500,
    zip: 6,
    houseNumber: 5,
};

export const getFormMaxlength = (key: string): number => {
    return FORM_MAX_LENGTH[key] ?? FORM_MAX_LENGTH.default;
};

export const isIframeSourceAllowed = (src: string): boolean => {
    const hostname = new URL(src).hostname;
    return hostname === "raabe.de" || hostname.endsWith(".raabe.de");
};

export function getEnumValue<T extends object, K = T[keyof T]>(
    str: string | undefined | null,
    enumObject: T,
    defaultValue: K,
): K;
export function getEnumValue<T extends object, K = T[keyof T]>(
    str: string | undefined | null,
    enumObject: T,
): K | undefined;
export function getEnumValue<T extends object, K = T[keyof T]>(
    str: string | undefined | null,
    enumObject: T,
    defaultValue?: K,
): K | undefined {
    // Check if str is defined and is a valid enum value
    return str !== undefined && str !== null && Object.values(enumObject).includes(str)
        ? (str as K)
        : defaultValue;
}

export function formatPrice(euros = 0): string {
    return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(euros);
}

export type SearchPageInfo = {
    id: string;
    name: string;
    parents: string[];
    slug: string;
    subpages: string[];
};
export type SearchPageInfos = Map<string, SearchPageInfo>;
export type DefaultPageInfo = {
    id: string;
    slug: string;
    title: string;
};
export type DefaultPageInfos = Map<string, DefaultPageInfo>;
export type BlogCategoryPageInfo = {
    id: string;
    slug: string;
    title: string;
};
export type BlogCategoryPageInfos = Map<string, BlogCategoryPageInfo>;
export type BlogArticlePageInfo = {
    id: string;
    slug: string;
};
export type BlogArticlePageInfos = Map<string, BlogArticlePageInfo>;
export type Context = {
    blogArticlePagesById: BlogArticlePageInfos;
    blogArticlePagesBySlug: BlogArticlePageInfos;
    blogCategoryPagesById: BlogCategoryPageInfos;
    blogCategoryPagesBySlug: BlogCategoryPageInfos;
    content: ContentType | undefined;
    defaultPagesById: DefaultPageInfos;
    defaultPagesBySlug: DefaultPageInfos;
    faecher: Record<string, string>;
    searchPagePathsById: Map<string, Set<string>>;
    searchPagesById: SearchPageInfos;
    searchPagesBySlug: Map<string, string>;
};

export enum ASPECT {
    "20/13" = "aspect-20/13",
    "5/4" = "aspect-5/4",
}
